// i18n Formatting Utilities
// Provides locale-aware formatting for dates, numbers, currency, and file sizes

import { getCurrentLanguage, getLanguageCode } from '../../services/i18n';

// Get locale string based on current language
const getLocale = (): string => {
  const lang = getCurrentLanguage();
  const localeMap: Record<string, string> = {
    'en': 'en-US',
    'fr': 'fr-FR',
    'ar': 'ar-SA',
    'darija': 'fr-FR', // Darija uses French locale for formatting
    'de': 'de-DE',
    'es': 'es-ES'
  };
  return localeMap[lang] || 'en-US';
};

// Get currency code based on target country (if available)
const getCurrencyCode = (country?: string): string => {
  const currencyMap: Record<string, string> = {
    'Morocco': 'EUR',
    'United States': 'USD',
    'United Kingdom': 'GBP',
    'France': 'EUR',
    'Canada': 'CAD',
    'Germany': 'EUR',
    'Spain': 'EUR',
    'Netherlands': 'EUR',
    'Belgium': 'EUR',
    'Switzerland': 'CHF',
    'Saudi Arabia': 'SAR',
    'UAE': 'AED',
    'Egypt': 'EGP',
    'Algeria': 'DZD',
    'Tunisia': 'TND',
    'Brazil': 'BRL',
    'Mexico': 'MXN',
    'Argentina': 'ARS',
    'Australia': 'AUD',
    'Japan': 'JPY',
    'South Korea': 'KRW',
    'China': 'CNY',
    'India': 'INR'
  };
  return currencyMap[country || ''] || 'USD';
};

/**
 * Format a number according to the current locale
 */
export const formatNumber = (value: number, options?: Intl.NumberFormatOptions): string => {
  try {
    const locale = getLocale();
    return new Intl.NumberFormat(locale, options).format(value);
  } catch (error) {
    // Fallback to default formatting
    return value.toLocaleString();
  }
};

/**
 * Format a number as a percentage
 */
export const formatPercentage = (value: number, decimals: number = 1): string => {
  try {
    const locale = getLocale();
    return new Intl.NumberFormat(locale, {
      style: 'percent',
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals
    }).format(value / 100);
  } catch (error) {
    return `${value.toFixed(decimals)}%`;
  }
};

/**
 * Format currency amount
 */
export const formatCurrency = (amount: number, country?: string, options?: Intl.NumberFormatOptions): string => {
  try {
    const locale = getLocale();
    const currency = getCurrencyCode(country);
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: currency,
      ...options
    }).format(amount);
  } catch (error) {
    // Fallback
    const currency = getCurrencyCode(country);
    return `${currency} ${amount.toFixed(2)}`;
  }
};

/**
 * Format file size (bytes to human-readable)
 */
export const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 Bytes';
  
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  
  const locale = getLocale();
  const formattedValue = formatNumber(bytes / Math.pow(k, i), {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });
  
  // Get translated size unit
  const sizeUnit = sizes[i] || 'Bytes';
  return `${formattedValue} ${sizeUnit}`;
};

/**
 * Format date according to locale
 */
export const formatDate = (date: Date | string | number, options?: Intl.DateTimeFormatOptions): string => {
  try {
    const dateObj = typeof date === 'string' || typeof date === 'number' ? new Date(date) : date;
    if (isNaN(dateObj.getTime())) {
      return 'Invalid Date';
    }
    
    const locale = getLocale();
    const defaultOptions: Intl.DateTimeFormatOptions = {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      ...options
    };
    
    return new Intl.DateTimeFormat(locale, defaultOptions).format(dateObj);
  } catch (error) {
    // Fallback
    const dateObj = typeof date === 'string' || typeof date === 'number' ? new Date(date) : date;
    return dateObj.toLocaleDateString();
  }
};

/**
 * Format date and time
 */
export const formatDateTime = (date: Date | string | number, options?: Intl.DateTimeFormatOptions): string => {
  try {
    const dateObj = typeof date === 'string' || typeof date === 'number' ? new Date(date) : date;
    if (isNaN(dateObj.getTime())) {
      return 'Invalid Date';
    }
    
    const locale = getLocale();
    const defaultOptions: Intl.DateTimeFormatOptions = {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      ...options
    };
    
    return new Intl.DateTimeFormat(locale, defaultOptions).format(dateObj);
  } catch (error) {
    // Fallback
    const dateObj = typeof date === 'string' || typeof date === 'number' ? new Date(date) : date;
    return dateObj.toLocaleString();
  }
};

/**
 * Format relative time (e.g., "2 hours ago", "in 3 days")
 */
export const formatRelativeTime = (date: Date | string | number): string => {
  try {
    const dateObj = typeof date === 'string' || typeof date === 'number' ? new Date(date) : date;
    if (isNaN(dateObj.getTime())) {
      return 'Invalid Date';
    }
    
    const now = new Date();
    const diffMs = dateObj.getTime() - now.getTime();
    const diffSeconds = Math.floor(diffMs / 1000);
    const diffMinutes = Math.floor(diffSeconds / 60);
    const diffHours = Math.floor(diffMinutes / 60);
    const diffDays = Math.floor(diffHours / 24);
    
    const locale = getLocale();
    const rtf = new Intl.RelativeTimeFormat(locale, { numeric: 'auto' });
    
    if (Math.abs(diffDays) > 7) {
      return formatDate(dateObj);
    } else if (Math.abs(diffDays) > 0) {
      return rtf.format(diffDays, 'day');
    } else if (Math.abs(diffHours) > 0) {
      return rtf.format(diffHours, 'hour');
    } else if (Math.abs(diffMinutes) > 0) {
      return rtf.format(diffMinutes, 'minute');
    } else {
      return rtf.format(diffSeconds, 'second');
    }
  } catch (error) {
    // Fallback
    return formatDate(date);
  }
};

/**
 * Format duration (e.g., "2h 30m", "45s")
 */
export const formatDuration = (seconds: number): string => {
  if (seconds < 60) {
    return `${Math.floor(seconds)}s`;
  } else if (seconds < 3600) {
    const minutes = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return secs > 0 ? `${minutes}m ${secs}s` : `${minutes}m`;
  } else {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    return minutes > 0 ? `${hours}h ${minutes}m` : `${hours}h`;
  }
};

/**
 * Format large numbers with abbreviations (e.g., 1.5K, 2.3M)
 */
export const formatCompactNumber = (value: number): string => {
  try {
    const locale = getLocale();
    return new Intl.NumberFormat(locale, {
      notation: 'compact',
      maximumFractionDigits: 1
    }).format(value);
  } catch (error) {
    // Fallback
    if (value >= 1000000) {
      return `${(value / 1000000).toFixed(1)}M`;
    } else if (value >= 1000) {
      return `${(value / 1000).toFixed(1)}K`;
    }
    return value.toString();
  }
};

